package dev.lingvo.ops.pages.workspaces.orgId

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.ops.api.OpsApi
import dev.lingvo.ops.pages.OpsResourcePagination
import dev.lingvo.ops.components.OpsCapNotice
import dev.lingvo.ops.components.OpsFact
import dev.lingvo.ops.components.OpsPageHeader
import dev.lingvo.ops.components.OpsPanel
import dev.lingvo.ops.components.OpsResourceStates
import dev.lingvo.ops.components.OpsShell
import dev.lingvo.ops.components.OpsUntrustedText
import dev.lingvo.ops.components.OpsWorkspaceNav
import dev.lingvo.ops.state.ledgerNotice
import dev.lingvo.ops.state.rememberOpsPaging
import dev.lingvo.ops.state.rememberOpsResource
import dev.lingvo.shared.ops.OpsCreditEventRowDto
import dev.lingvo.shared.ops.OpsPaddleEventRowDto
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.formatUtc
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Page("/workspaces/{orgId}/billing")
@Composable
fun BillingPage() {
    val id = rememberPageContext().route.params["orgId"].orEmpty()
    OpsShell("Workspaces") {
        val creditPaging = rememberOpsPaging<OpsCreditEventRowDto>(id)
        val eventPaging = rememberOpsPaging<OpsPaddleEventRowDto>(id)
        val billing = rememberOpsResource(id, OpsApi::billing)
        val credits = rememberOpsResource(id to creditPaging.cursor) { (orgId, cursor) -> OpsApi.credits(orgId, cursor) }
        val grants = rememberOpsResource(id, OpsApi::grants)
        val checkouts = rememberOpsResource(id, OpsApi::checkouts)
        val documents = rememberOpsResource(id, OpsApi::documents)
        val cancellations = rememberOpsResource(id, OpsApi::cancellations)
        val events = rememberOpsResource(id to eventPaging.cursor) { (orgId, cursor) -> OpsApi.paddleEvents(orgId, null, cursor) }
        fun refresh() { billing.refresh(); credits.refresh(); grants.refresh(); checkouts.refresh(); documents.refresh(); cancellations.refresh(); events.refresh() }
        val refreshing = billing.state.isRefreshing || credits.state.isRefreshing || grants.state.isRefreshing ||
            checkouts.state.isRefreshing || documents.state.isRefreshing || cancellations.state.isRefreshing || events.state.isRefreshing
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            OpsPageHeader("Workspace billing", "Compare recorded entitlements, credit activity and processor evidence. Billing changes stay in Paddle.", refreshing, ::refresh)
            OpsWorkspaceNav(id, "Billing")
            OpsResourceStates(billing.state, "No billing record", "This workspace has no accessible billing state.", retry = billing::refresh) { value ->
                OpsPanel("Plan and entitlement") {
                    OpsFact("Plan", value.plan.displayName)
                    OpsFact("Status", value.planStatus.name)
                    OpsFact("Renews or ends", formatUtc(value.renewsAt, absent = "Not recorded"))
                    OpsFact("Subscription", if (value.hasSubscription) "Linked" else "None")
                    OpsFact("Customer", if (value.hasCustomer) "Linked" else "None")
                    OpsFact("Subscriptions recorded", formatCount(value.subscriptionCount))
                }
                OpsPanel("Current credit allowance", "Customer credits and provider invoice amounts are separate records.") {
                    OpsFact("Included", formatCount(value.creditsIncluded))
                    OpsFact("Granted", formatCount(value.creditsGranted))
                    OpsFact("Used", formatCount(value.creditsUsed))
                    OpsFact("Period begins", formatUtc(value.periodStart))
                }
                ledgerNotice(value.periodStart, value.ledgerBegins)?.let { note -> OpsPanel("Ledger boundary") { P { Text(note) } } }
            }
            OpsPanel("Credit events", "Keyset-paged records. The current page is not a total for the billing period.") {
                OpsResourceStates(credits.state, "No credit events", "No credit events were returned in this page.", retry = credits::refresh) { page ->
                    page.rows.forEach { row ->
                        P { Text("${formatUtc(row.createdAt)} · ${row.kind} · ${formatCount(row.credits)} credits · ${row.projectName ?: "No project"} · ${row.provider ?: "No provider"}") }
                    }
                }
                OpsResourcePagination(creditPaging, credits.state, "credit events")
            }
            OpsPanel("Credit grants") {
                OpsResourceStates(grants.state, "No grants in this window", "No credit grants were returned.", retry = grants::refresh) { window ->
                    OpsCapNotice(window.hitCap, window.cappedAt, "grants")
                    window.rows.forEach { row -> P { Text("${formatUtc(row.createdAt)} · ${formatCount(row.credits)} credits · ${row.period} · ${row.reason}") } }
                }
            }
            OpsPanel("Checkouts", "A created checkout is not payment confirmation. Compare settled state with processor events and documents.") {
                OpsResourceStates(checkouts.state, "No checkouts in this window", "No checkout records were returned.", retry = checkouts::refresh) { window ->
                    OpsCapNotice(window.hitCap, window.cappedAt, "checkouts")
                    window.rows.forEach { row -> P { Text("${row.plan.displayName} · ${formatCount(row.seats)} seats · created ${formatUtc(row.createdAt)} · ${row.settledAt?.let { "settled ${formatUtc(it)}" } ?: "not settled"}") } }
                }
            }
            OpsPanel("Billing documents", "Cached documents, not a live processor balance. Refunds appear as separate negative rows.") {
                OpsResourceStates(documents.state, "No cached billing documents", "No documents were returned. This does not establish whether a charge occurred.", retry = documents::refresh) { window ->
                    OpsCapNotice(window.hitCap, window.cappedAt, "documents")
                    window.rows.forEach { row -> P { Text("${row.invoiceNumber ?: "Document without an invoice number"} · ${row.amount.display} · ${row.status} · ${formatUtc(row.billedAt)}") } }
                }
            }
            OpsPanel("Cancellation records") {
                OpsResourceStates(cancellations.state, "No cancellations in this window", "No cancellation records were returned.", retry = cancellations::refresh) { window ->
                    OpsCapNotice(window.hitCap, window.cappedAt, "cancellations")
                    window.rows.forEach { row ->
                        P { Text("${row.plan.displayName} · recorded ${formatUtc(row.createdAt)} · effective ${formatUtc(row.effectiveAt)} · ${row.reason ?: "No reason recorded"}") }
                        row.comment?.let { P { OpsUntrustedText(it) } }
                    }
                }
            }
            OpsPanel("Paddle webhook events", "Event ids identify our own webhook deliveries, never a customer's billing relationship. Receipt alone does not establish successful fulfilment.") {
                OpsResourceStates(events.state, "No events in this page", "No Paddle events were returned in this page.", retry = events::refresh) { page ->
                    page.rows.forEach { row -> P { Text("${formatUtc(row.receivedAt)} · ${row.eventType} · ${if (row.linked) "linked" else "unlinked"} · ${row.summary}") } }
                }
                OpsResourcePagination(eventPaging, events.state, "events")
            }
        }
    }
}
